import { Component } from '@theme/component';

const COLLAPSE_DELAY_MS = 3000;

/**
 * Shared quantity stepper pill (trash/minus/plus), used by both box-builder
 * (sections/box-builder.liquid, `data-mode="free"`) and the bundle-recipe-builder
 * (snippets/bundle-recipe-slot-card.liquid, `data-mode="toggle"`). Owns the full state machine
 * so neither caller has to poke its DOM by hand:
 *
 * Both modes share the same collapse/expand mechanic: starts (and settles back to, after 3s
 * idle) "collapsed" — just the value badge, in an outline circle. Tapping it expands to
 * trash/minus · value · plus; interacting with those resets the 3s idle clock.
 *
 * - `mode="free"`: quantity floor 0. At 0 the pill drops the "collapsed" outline for "empty" —
 *   just the plus button, no idle timer (there's nothing to collapse back to). The remove button
 *   shows trash at quantity 1 (click removes entirely) and minus at quantity >=2 (click decrements).
 * - `mode="toggle"`: quantity floor 1, so it never hits "empty" — always either expanded or
 *   collapsed. `data-active` (mirrors a companion checkbox the caller owns) only drives the
 *   outline's color intensity, never its shape. Remove at quantity 1 sets active=false (quantity
 *   is preserved, not zeroed); plus while inactive just reactivates without changing quantity.
 *
 * Callers don't manipulate this element's internals directly — they call `setQuantity`/`setActive`
 * and listen for `quantity-stepper:change` (bubbles, `detail: {quantity, active}`) to sync their
 * own bookkeeping (cart map, rail, checkbox), and for the cancelable `quantity-stepper:beforeincrease`
 * to intercept a fresh plus-click in `mode="free"` (box-builder uses this to open its variant/
 * preferences picker instead of incrementing straight away).
 */
class QuantityStepperComponent extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #collapseTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncRemoveButton();
    this.#syncPlusButton();

    if (this.dataset.state === 'expanded') this.#scheduleCollapse();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearCollapseTimer();
  }

  /** @returns {'free' | 'toggle'} */
  get mode() {
    return this.dataset.mode === 'toggle' ? 'toggle' : 'free';
  }

  /** @returns {number} */
  get quantity() {
    return Number(this.dataset.quantity) || 0;
  }

  /** @returns {number} Voorraadlimiet (bv. inventory), Infinity als geen `data-max` is gezet. */
  get max() {
    return this.dataset.max != null ? Number(this.dataset.max) : Infinity;
  }

  /** @returns {boolean} */
  get active() {
    return this.mode === 'free' ? this.quantity > 0 : this.dataset.active !== 'false';
  }

  /** @param {Event} event */
  handleRemove(event) {
    event.preventDefault();
    if (this.querySelector('.quantity-stepper__remove')?.hasAttribute('disabled')) return;

    if (this.quantity >= 2) {
      this.setQuantity(this.quantity - 1);
    } else if (this.mode === 'free') {
      this.setQuantity(0);
    } else {
      this.setActive(false);
    }
  }

  /** @param {Event} event */
  handlePlus(event) {
    event.preventDefault();
    if (this.querySelector('.quantity-stepper__plus')?.hasAttribute('disabled')) return;

    if (this.mode === 'toggle' && !this.active) {
      this.setActive(true);
      return;
    }

    if (this.mode === 'free') {
      const beforeEvent = new CustomEvent('quantity-stepper:beforeincrease', { bubbles: true, cancelable: true });
      this.dispatchEvent(beforeEvent);
      if (beforeEvent.defaultPrevented) return;
    }

    this.setQuantity(this.quantity + 1);
  }

  /** @param {Event} event */
  handlePillTap(event) {
    event.preventDefault();
    if (this.dataset.state !== 'collapsed') return;

    this.dataset.state = 'expanded';
    this.#scheduleCollapse();
  }

  /**
   * @param {number} value
   * @param {{silent?: boolean}} [options]
   */
  setQuantity(value, options = {}) {
    const min = this.mode === 'toggle' ? 1 : 0;
    const next = Math.min(this.max, Math.max(min, Math.floor(value)));
    const changed = next !== this.quantity;

    this.dataset.quantity = String(next);

    const valueText = this.querySelector('.quantity-stepper__value-text');
    if (valueText) valueText.textContent = String(next);

    this.#syncRemoveButton();
    this.#syncPlusButton();

    if (this.mode === 'free' && next === 0) {
      this.#clearCollapseTimer();
      this.dataset.state = 'empty';
    } else {
      this.dataset.state = 'expanded';
      this.#scheduleCollapse();
    }

    if (changed && !options.silent) this.#dispatchChange();
  }

  /**
   * Doesn't force the pill open/closed by itself — only recolors the outline (via
   * #syncRemoveButton/CSS keyed off data-active) and, if it's currently expanded (the trash
   * click that set active=false only fires from there), resets the idle clock like any other
   * interaction so it settles back to "collapsed" the same way a quantity change would.
   * @param {boolean} value
   * @param {{silent?: boolean}} [options]
   */
  setActive(value, options = {}) {
    if (this.mode !== 'toggle') return;

    const next = Boolean(value);
    const changed = next !== this.active;

    this.dataset.active = String(next);
    this.#syncRemoveButton();

    if (this.dataset.state === 'expanded') this.#scheduleCollapse();

    if (changed && !options.silent) this.#dispatchChange();
  }

  #syncRemoveButton() {
    const removeBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.quantity-stepper__remove'));
    if (!removeBtn) return;

    const isDecrease = this.quantity >= 2;
    removeBtn.classList.toggle('is-decrease', isDecrease);
    removeBtn.setAttribute(
      'aria-label',
      isDecrease ? 'Verminder aantal' : this.mode === 'free' ? 'Verwijder uit pakket' : 'Verwijder uit selectie'
    );

    if (this.mode === 'toggle') removeBtn.disabled = !this.active;
  }

  /** Disables the plus button once quantity has reached `max` (e.g. available inventory). */
  #syncPlusButton() {
    const plusBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.quantity-stepper__plus'));
    if (plusBtn) plusBtn.disabled = this.quantity >= this.max;
  }

  #scheduleCollapse() {
    this.#clearCollapseTimer();
    this.#collapseTimer = setTimeout(() => {
      this.dataset.state = 'collapsed';
      this.#collapseTimer = null;
    }, COLLAPSE_DELAY_MS);
  }

  #clearCollapseTimer() {
    if (this.#collapseTimer == null) return;
    clearTimeout(this.#collapseTimer);
    this.#collapseTimer = null;
  }

  #dispatchChange() {
    this.dispatchEvent(
      new CustomEvent('quantity-stepper:change', {
        bubbles: true,
        detail: { quantity: this.quantity, active: this.active },
      })
    );
  }
}

if (!customElements.get('quantity-stepper-component')) {
  customElements.define('quantity-stepper-component', QuantityStepperComponent);
}
